/**
 * Validation of data read back from storage. Stored data may come from an older app
 * version or be edited by hand, so it is checked field by field before use.
 */
import {
  AVATAR_IDS,
  COLOR_IDS,
  ICON_IDS,
  type PlayerProfile,
  sanitizeName,
  TRAIL_IDS,
} from '@cube-rush/shared';

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isOneOf<T extends string>(options: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (options as readonly string[]).includes(value);
}

/** A complete, valid profile or null. The name is sanitized like the server does it. */
export function parseProfile(raw: unknown): PlayerProfile | null {
  if (!isRecord(raw) || !isRecord(raw.skin)) return null;
  const name = typeof raw.name === 'string' ? sanitizeName(raw.name) : '';
  const { avatar } = raw;
  const { icon, primary, secondary, trail } = raw.skin;
  if (
    name === '' ||
    !isOneOf(AVATAR_IDS, avatar) ||
    !isOneOf(ICON_IDS, icon) ||
    !isOneOf(COLOR_IDS, primary) ||
    !isOneOf(COLOR_IDS, secondary) ||
    !isOneOf(TRAIL_IDS, trail)
  ) {
    return null;
  }
  return { name, avatar, skin: { icon, primary, secondary, trail } };
}

/** A number in [0, 1], or `fallback` when the value is not a finite number. */
export function unitInterval(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}

export function booleanOr(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}
